/**
 * VentasDiaPage — Reservas CREADAS hoy (lente C · comercial).
 *
 * Mide productividad comercial del día: qué se vendió hoy, sin importar
 * cuándo son las salidas ni si el cobro ya entró. Complementa al Cierre
 * de Muelle (que agrupa por fecha de servicio) y a Ingresos por cuenta
 * (que agrupa por fecha del pago).
 *
 * Endpoint: GET /reportes/ventas-dia?empresa_id=&fecha=YYYY-MM-DD
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box, Typography, Card, CardContent, TextField, IconButton, Chip, Button,
  Table, TableHead, TableRow, TableCell, TableBody, TableFooter, TableContainer, Paper,
  CircularProgress, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  Collapse, Popover, MenuItem, Select,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { API_BASE, getAuthHeaders } from '../booking/shared/format';

function _hoyIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
// Suma/resta N días a un ISO YYYY-MM-DD y devuelve el resultado en el mismo
// formato. Se construye con Date locale para respetar la zona horaria del
// operador (importante en Bogotá para evitar off-by-one).
function _shiftDia(iso, delta) {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + delta);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
}
// "2026-08-17" → "Sáb 17 Ago 2026" (formato humano con día de semana).
function _fechaHumana(iso) {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  const dias  = ['Dom','Lun','Mar','Mié','Jue','Vie','Sáb'];
  const meses = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
  return `${dias[dt.getDay()]} ${d} ${meses[m - 1]} ${y}`;
}
// Chip contextual: HOY / AYER / MAÑANA / Hace Nd / En Nd — según diferencia
// con la fecha actual. Devuelve {label, bg, color} o null si no aplica.
function _chipContexto(iso) {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  const target = new Date(y, m - 1, d);
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  const diff = Math.round((target - hoy) / (1000 * 60 * 60 * 24));
  if (diff === 0)  return { label: 'HOY',    bg: '#dcfce7', color: '#15803d' };
  if (diff === -1) return { label: 'AYER',   bg: '#f1f5f9', color: '#475569' };
  if (diff ===  1) return { label: 'MAÑANA', bg: '#dbeafe', color: '#1d4ed8' };
  if (diff <  -1)  return { label: `Hace ${-diff}d`, bg: '#f1f5f9', color: '#64748b' };
  return { label: `En ${diff}d`, bg: '#cffafe', color: '#0891b2' };
}
function fmt(n) {
  const v = Number(n || 0);
  return `$${new Intl.NumberFormat('es-CO', { maximumFractionDigits: 0 }).format(v)}`;
}
// Etiqueta amigable para el método/origen del pago (cuadre de caja).
const _METODO_LABEL = {
  efectivo:              'Efectivo',
  tarjeta_credito:       'Tarjeta crédito',
  tarjeta_debito:        'Tarjeta débito',
  bold_link:             'Bold (link)',
  transferencia:         'Transferencia',
  pago_directo_cliente:  'Transferencia directa',
  pago_agencia:          'Transferencia agencia',
  muelle:                'Pago en muelle',
  manual:                'Ajuste manual',
  cruce:                 'Cruce comisión (sin voucher)',
  compensacion:          'Compensación',
  otro:                  'Otro',
};
function _labelMetodo(m) {
  if (!m) return 'Otro';
  return _METODO_LABEL[m] || m.replace(/_/g, ' ');
}
function _fechaCorta(iso) {
  if (!iso) return '—';
  // "2026-08-20" → "20-ago"
  const [y, mm, dd] = iso.split('-');
  const meses = ['ene','feb','mar','abr','may','jun','jul','ago','sep','oct','nov','dic'];
  const idx = parseInt(mm, 10) - 1;
  return `${dd}-${meses[idx] || mm}`;
}
// Arma el contenido de tooltip para una celda de cobros.
// Cada pago se lista como: "20-ago · Transferencia · $500.000"
function _tooltipPagos(pagos = [], labelVacio = 'Sin cobros') {
  if (!pagos || pagos.length === 0) return labelVacio;
  return pagos
    .map(p => `${_fechaCorta(p.fecha)} · ${_labelMetodo(p.metodo)} · ${fmt(p.monto)}`)
    .join('\n');
}
const _empresa = () => localStorage.getItem('empresa_id') || localStorage.getItem('company_id') || '1';

export default function VentasDiaPage() {
  const [fecha, setFecha] = useState(_hoyIso());
  // Lente de la pantalla. Son dos preguntas distintas sobre los mismos datos:
  //   'venta'  → agrupa por fecha de CREACIÓN. "¿Cuánto vendió el equipo ese
  //              día?" Mide productividad comercial. Es el comportamiento
  //              histórico de esta pantalla y no se toca.
  //   'salida' → agrupa por fecha de SERVICIO. "¿Qué tengo vendido y para
  //              cuándo?" Existe porque con flechas de un día en un día las
  //              ventas dispersas no se encuentran: hay reservas a meses
  //              vista que por ese camino nadie ve nunca.
  const [lente, setLente] = useState('venta');
  const [diasAbiertos, setDiasAbiertos] = useState(new Set());
  const toggleDia = (k) => {
    setDiasAbiertos(prev => {
      const n = new Set(prev);
      if (n.has(k)) n.delete(k); else n.add(k);
      return n;
    });
  };
  // Estado global de esta pantalla para el lightbox de voucher — reutilizado
  // desde la tabla de reservas y desde el ranking expandido de agencias.
  const [voucherLightbox, setVoucherLightbox] = useState(null);   // {id, url, ...}
  // Set con las agencias expandidas (ranking) — permite abrir varias a la vez.
  const [agenciasAbiertas, setAgenciasAbiertas] = useState(new Set());
  const toggleAgencia = (k) => {
    setAgenciasAbiertas(prev => {
      const s = new Set(prev);
      if (s.has(k)) s.delete(k); else s.add(k);
      return s;
    });
  };
  // Preset: 0 = hoy, -1 = ayer, +1 = mañana (deltas respecto a hoy).
  const preset = (delta) => setFecha(_shiftDia(_hoyIso(), delta));
  // Distancia entre `fecha` y hoy — sirve para pintar los presets activos y
  // el chip contextual sin recalcular en cada componente.
  const _diffHoy = (() => {
    const [y, m, d] = fecha.split('-').map(Number);
    const target = new Date(y, m - 1, d);
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    return Math.round((target - hoy) / (1000 * 60 * 60 * 24));
  })();
  const chipContexto = _chipContexto(fecha);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['ventas-dia', fecha],
    queryFn: async () => {
      const r = await fetch(
        `${API_BASE}/reportes/ventas-dia?empresa_id=${_empresa()}&fecha=${fecha}`,
        { headers: getAuthHeaders() }
      );
      if (!r.ok) throw new Error(`Error ${r.status}`);
      return r.json();
    },
    staleTime: 60_000,
  });

  // El detalle de cada día viaja en la misma respuesta: con 13 días y 37
  // reservas pesa poco, y evita una petición por cada día que se despliega.
  const { data: dataSalida, isLoading: cargandoSalida, isFetching: refrescandoSalida,
          refetch: refetchSalida } = useQuery({
    queryKey: ['ventas-por-salida'],
    queryFn: async () => {
      const r = await fetch(
        `${API_BASE}/reportes/ventas-por-salida?empresa_id=${_empresa()}&incluir_reservas=1`,
        { headers: getAuthHeaders() }
      );
      if (!r.ok) throw new Error(`Error ${r.status}`);
      return r.json();
    },
    enabled: lente === 'salida',
    staleTime: 60_000,
  });

  const resumen = data?.resumen || {};
  const agencias = data?.por_agencia || [];
  const salidasFuturas = data?.por_salida_futura || [];
  const reservas = data?.reservas || [];
  // Charters se identifican por prefijo CHT- (patrón consistente en el sistema).
  // Alternativa: podría exponerse tipo_reservacion en el endpoint, pero el
  // prefijo del código funciona bien y no requiere cambios backend.
  const reservasPasadias = reservas.filter(r => !((r.codigo || '').startsWith('CHT-')));
  const reservasCharter  = reservas.filter(r => (r.codigo || '').startsWith('CHT-'));
  // % cobrado AL DÍA seleccionado — no incluye pagos posteriores (evolución).
  // Con fallback a `ya_cobrado` para compatibilidad con versiones previas del endpoint.
  const yaCobradoAlDia = resumen.ya_cobrado_al_dia ?? resumen.ya_cobrado ?? 0;
  const pctCobrado = resumen.pvp_total > 0
    ? (yaCobradoAlDia / resumen.pvp_total * 100).toFixed(1)
    : 0;

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, bgcolor: '#f8fafc', minHeight: '100vh',
                overflowX: 'hidden' }}>
      {/* Toolbar */}
      <Box sx={{ display: 'flex', alignItems: { xs: 'stretch', sm: 'center' },
                  gap: 1, mb: 2, flexDirection: { xs: 'column', sm: 'row' },
                  flexWrap: 'wrap' }}>
        {/* En móvil la toolbar es columna, y ahí `flex-basis: 280px` deja de
            significar ancho y pasa a significar ALTO: dos renglones de título
            ocupando 280px de pantalla en blanco. El basis sólo aplica cuando
            la toolbar es fila. */}
        <Box sx={{ flex: { xs: '0 0 auto', sm: '1 1 280px' }, minWidth: 0 }}>
          <Typography sx={{ fontSize: { xs: 18, md: 22 }, fontWeight: 800, color: '#0f172a' }}>
            Venta diaria
          </Typography>
          <Typography sx={{ fontSize: 11, color: '#64748b' }}>
            {lente === 'venta'
              ? 'Reservas ejecutadas en la fecha seleccionada · lo que se vendió y cómo se está recaudando · pasadías presentes o futuras'
              : 'Todos los días que ya tienen ventas · agrupado por la fecha en que sale la gente · para ver el libro hacia adelante'}
          </Typography>
        </Box>

        {/* Cambio de lente. Deliberadamente explícito y no un filtro más:
            "por fecha de venta" y "por fecha de salida" responden preguntas
            distintas y confundirlas lleva a leer mal las cifras. */}
        <Box sx={{ display: 'flex', gap: 0.5, bgcolor: '#e2e8f0', p: 0.4,
                    borderRadius: 999, flexShrink: 0 }}>
          {[
            { k: 'venta',  label: 'Fecha de venta'  },
            { k: 'salida', label: 'Fecha de salida' },
          ].map(o => (
            <Box key={o.k} onClick={() => setLente(o.k)} role="button" tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setLente(o.k); }}
              sx={{
                px: 1.5, py: 0.6, borderRadius: 999, cursor: 'pointer',
                fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap',
                bgcolor: lente === o.k ? '#fff' : 'transparent',
                color:   lente === o.k ? '#0f172a' : '#64748b',
                boxShadow: lente === o.k ? '0 1px 3px rgba(15,23,42,.14)' : 'none',
                transition: 'all .15s',
              }}>
              {o.label}
            </Box>
          ))}
        </Box>
        {lente === 'venta' && (
        <>
        {/* Selector de fecha rediseñado — A+B+C:
              A · Navegación ‹ Fecha › con formato humano.
              B · Presets Ayer/Hoy/Mañana con estado activo visual.
              C · Chip contextual (HOY/AYER/MAÑANA/Hace Nd/En Nd).
            El TextField type=date sigue existiendo pero OCULTO detrás de
            la etiqueta central — se dispara al hacer click en ella para
            saltos largos, sin ocupar espacio visual. */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Presets — chips con estado activo (B) */}
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {[
              { delta: -1, label: 'Ayer'   },
              { delta:  0, label: 'Hoy'    },
              { delta: +1, label: 'Mañana' },
            ].map(p => {
              const activo = _diffHoy === p.delta;
              return (
                <Chip key={p.delta} label={p.label} size="small"
                  onClick={() => preset(p.delta)}
                  sx={{
                    fontSize: 11, height: 26, fontWeight: 700, cursor: 'pointer',
                    bgcolor: activo ? '#0d9488' : '#f1f5f9',
                    color:   activo ? '#fff'    : '#475569',
                    '&:hover': { bgcolor: activo ? '#0f766e' : '#e2e8f0' },
                  }} />
              );
            })}
          </Box>

          {/* Navegación día por día + botón que abre calendario visual.
              Las flechas ‹ › siguen para saltos de 1 día (patrón habitual).
              El botón central abre un calendario mensual real (Popover)
              donde puedes navegar por mes/año y clickear cualquier día —
              es la única forma confiable de saltar a fechas lejanas
              (ej. 30-dic desde 17-ago). El input nativo del navegador
              se removió porque su comportamiento oculto era inconsistente. */}
          <Box sx={{ display: 'flex', alignItems: 'center',
                      bgcolor: '#f1f5f9', borderRadius: 2, px: 0.5, py: 0.25 }}>
            <Tooltip title="Día anterior" arrow>
              <IconButton size="small" onClick={() => setFecha(_shiftDia(fecha, -1))}
                sx={{ color: '#0d9488', p: 0.5 }}>
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <DateCalendarPopover valor={fecha} onChange={setFecha}>
              <Button variant="text"
                startIcon={<CalendarTodayIcon sx={{ fontSize: 14 }} />}
                endIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}
                sx={{
                  textTransform: 'capitalize',
                  fontSize: 13, fontWeight: 700, color: '#0f172a',
                  px: 1.25, py: 0.5, minWidth: 0,
                  '&:hover': {
                    bgcolor: '#fff',
                    boxShadow: '0 0 0 1px #0d9488',
                    color: '#0d9488',
                  },
                }}>
                {_fechaHumana(fecha)}
                {chipContexto && (
                  <Chip size="small" label={chipContexto.label}
                    sx={{ ml: 0.75, height: 18, fontSize: 9, fontWeight: 700,
                          bgcolor: chipContexto.bg, color: chipContexto.color }} />
                )}
              </Button>
            </DateCalendarPopover>
            <Tooltip title="Día siguiente" arrow>
              <IconButton size="small" onClick={() => setFecha(_shiftDia(fecha, +1))}
                sx={{ color: '#0d9488', p: 0.5 }}>
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <IconButton onClick={refetch} disabled={isFetching} size="small"
            sx={{ color: '#0d9488' }}>
            <RefreshIcon sx={{ ...(isFetching && { animation: 'spin 1s linear infinite' }) }} />
          </IconButton>
        </Box>
        </>
        )}
      </Box>

      {lente === 'venta' && (isLoading || !data ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}><CircularProgress /></Box>
      ) : (
        <>
          {/* KPIs */}
          <Card sx={{ mb: 2, borderRadius: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#0f172a' }}>
                  Resumen del día · {resumen.num_reservas} {resumen.num_reservas === 1 ? 'reserva' : 'reservas'}
                </Typography>
                <Chip label={`${pctCobrado}% cobrado`} size="small"
                  sx={{ bgcolor: pctCobrado >= 80 ? '#dcfce7' : pctCobrado >= 50 ? '#fef3c7' : '#fee2e2',
                        color: pctCobrado >= 80 ? '#15803d' : pctCobrado >= 50 ? '#a16207' : '#b91c1c',
                        fontWeight: 800, fontSize: 12 }} />
              </Box>
              {/* Grid con alignItems stretch: todas las tarjetas comparten
                  la altura del row, quedan uniformes. gridAutoRows 1fr fuerza
                  filas del mismo alto cuando el grid wrapea. */}
              <Box sx={{ display: 'grid',
                          gridTemplateColumns: { xs: 'repeat(2,1fr)', sm: 'repeat(3,1fr)', md: 'repeat(4,1fr)', lg: 'repeat(7,1fr)' },
                          gridAutoRows: '1fr',
                          alignItems: 'stretch',
                          gap: 1 }}>
                <TileConBreakdown
                  label="PVP vendido"
                  total={resumen.pvp_total}
                  subA={{ label: 'Pasadías', v: resumen.pvp_pasadias, n: resumen.num_pasadias }}
                  subB={{ label: 'Charter',  v: resumen.pvp_charter,  n: resumen.num_charter }}
                  color="#0f172a" bg="#f1f5f9"
                />
                <TileConBreakdown
                  label="Neto Entretur"
                  total={resumen.neto_entretur}
                  subA={{ label: 'Pasadías', v: resumen.neto_entretur_pasadias, n: resumen.num_pasadias }}
                  subB={{ label: 'Charter',  v: resumen.neto_entretur_charter,  n: resumen.num_charter }}
                  color="#15803d" bg="#dcfce7"
                />
                <Tile label="Comis. anticipada" v={resumen.comision_anticipada} color="#7c3aed" bg="#ede9fe"
                       hint="La agencia ya la retuvo — no la debemos" />
                <Tile label="Comis. por pagar"  v={resumen.comision_por_pagar}  color="#be185d" bg="#fce7f3"
                       hint="Entretur aún debe liquidar a la agencia" />
                <Tile label="✅ Cobrado al día" v={yaCobradoAlDia}              color="#0d9488" bg="#ccfbf1"
                       hint="Suma de cobros hechos hasta el día seleccionado inclusive (no incluye pagos posteriores)" />
                <Tile label="🟡 Por cobrar muelle" v={resumen.por_cobrar_muelle} color="#a16207" bg="#fef3c7" />
                <Tile label="🔴 Por cobrar agencia" v={resumen.por_cobrar_agencia} color="#b91c1c" bg="#fee2e2" />
              </Box>

              {/* Desglose del cobrado: HOY vs ANTES (ingreso real a cuentas).
                  Solo aparece si hubo cobros previos — evita ruido cuando toda
                  la venta es "reservo y pago hoy mismo". */}
              {(resumen.cobrado_antes || 0) > 0 && (
                <Box sx={{ display: 'flex', gap: 1, mt: 1.25, flexWrap: 'wrap',
                            p: 0.75, bgcolor: '#f0fdf4', borderRadius: 1,
                            border: '1px dashed #86efac' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: '1 1 180px' }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%',
                                bgcolor: '#16a34a', flexShrink: 0 }} />
                    <Typography sx={{ fontSize: 10, color: '#475569', fontWeight: 600 }}>
                      Cobrado HOY (ingreso del día)
                    </Typography>
                    <Typography sx={{ fontSize: 11, fontWeight: 800, color: '#15803d',
                                       ml: 'auto', fontFamily: 'ui-monospace, monospace' }}>
                      ${new Intl.NumberFormat('es-CO').format(Number(resumen.cobrado_hoy || 0))}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: '1 1 180px' }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%',
                                bgcolor: '#0891b2', flexShrink: 0 }} />
                    <Tooltip title="Cobros hechos en días anteriores (ej. anticipos que se registraron al momento pero el pago original fue días atrás)">
                      <Typography sx={{ fontSize: 10, color: '#475569', fontWeight: 600,
                                         cursor: 'help', borderBottom: '1px dotted #94a3b8' }}>
                        Cobrado antes (previos)
                      </Typography>
                    </Tooltip>
                    <Typography sx={{ fontSize: 11, fontWeight: 800, color: '#0891b2',
                                       ml: 'auto', fontFamily: 'ui-monospace, monospace' }}>
                      ${new Intl.NumberFormat('es-CO').format(Number(resumen.cobrado_antes || 0))}
                    </Typography>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Reservas creadas hoy — divididas por tipo: pasadías vs charter.
              Charters usan prefijo CHT- y NO llevan comisión (venta directa
              Entretur), por eso conviene verlos aparte para comparar métricas. */}
          <ReservasTable
            titulo="Pasadías creadas hoy"
            reservas={reservasPasadias}
            colorHeader="#0d9488"
            onVerVoucher={setVoucherLightbox}
          />
          {reservasCharter.length > 0 && (
            <ReservasTable
              titulo="Charters creados hoy"
              reservas={reservasCharter}
              colorHeader="#14b8a6"
              onVerVoucher={setVoucherLightbox}
              esCharter
            />
          )}

          {/* Ranking por AGENCIA — quién realmente vende (el comercial solo
              digita en el sistema). Las reservas sin agencia caen al bucket
              "Venta directa Entretur". */}
          <Card sx={{ mb: 2, borderRadius: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#0f172a', mb: 0.25 }}>
                Ranking por agencia (mayor venta primero)
              </Typography>
              <Typography sx={{ fontSize: 10, color: '#94a3b8', mb: 1, fontStyle: 'italic' }}>
                Solo pasadías · los charters se contabilizan aparte
              </Typography>
              {agencias.length === 0 ? (
                <Typography sx={{ fontSize: 12, color: '#64748b' }}>Sin ventas en el día.</Typography>
              ) : (
                <TableContainer component={Paper}
                  sx={{ boxShadow: 'none', border: '1px solid #e2e8f0',
                        overflowX: 'auto' }}>
                  <Table size="small" sx={{ minWidth: 720 }}>
                    <TableHead sx={{ bgcolor: '#f8fafc' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Agencia</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, fontSize: 11 }}>Reservas</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11 }}>PVP</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11,
                                                        display: { xs: 'none', sm: 'table-cell' } }}>Comisión</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11,
                                                        display: { xs: 'none', md: 'table-cell' } }}>Neto Entretur</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11 }}>Cobrado</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11,
                                                        display: { xs: 'none', md: 'table-cell' } }}>Por cobrar</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, fontSize: 11 }}>% cobrado</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {agencias.map(a => {
                        const pct = a.pvp > 0 ? (a.cobrado / a.pvp * 100).toFixed(0) : 0;
                        const key = a.agencia_id || 'directa';
                        const abierta = agenciasAbiertas.has(key);
                        // Reservas de esta agencia (excluyendo charters — no
                        // están en el ranking pero la lista `reservas` sí las
                        // incluye; filtramos por agencia_id).
                        const misReservas = reservas.filter(
                          rr => (rr.agencia_id || null) === (a.agencia_id || null)
                                && (a.agencia_id || null) !== null  // ignoramos "directa"
                        );
                        return (
                          <React.Fragment key={key}>
                            <TableRow hover onClick={() => toggleAgencia(key)}
                              sx={{ cursor: 'pointer',
                                    '&:hover': { bgcolor: '#f0fdfa' } }}>
                              <TableCell sx={{ fontSize: 12, fontWeight: 600, color: '#0f172a' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <ExpandMoreIcon sx={{ fontSize: 16, color: '#0d9488',
                                    transition: 'transform 0.2s',
                                    transform: abierta ? 'rotate(180deg)' : 'none' }} />
                                  {a.nombre}
                                </Box>
                              </TableCell>
                              <TableCell align="center" sx={{ fontSize: 12 }}>{a.reservas}</TableCell>
                              <TableCell align="right" sx={{ fontSize: 12, fontFamily: 'monospace' }}>{fmt(a.pvp)}</TableCell>
                              <TableCell align="right" sx={{ fontSize: 12, fontFamily: 'monospace',
                                                              color: a.comision > 0 ? '#7c3aed' : '#94a3b8',
                                                              display: { xs: 'none', sm: 'table-cell' } }}>
                                {fmt(a.comision)}
                              </TableCell>
                              <TableCell align="right" sx={{ fontSize: 12, fontFamily: 'monospace', color: '#15803d',
                                                              display: { xs: 'none', md: 'table-cell' } }}>{fmt(a.neto)}</TableCell>
                              <TableCell align="right" sx={{ fontSize: 12, fontFamily: 'monospace', color: '#0d9488' }}>{fmt(a.cobrado)}</TableCell>
                              <TableCell align="right" sx={{ fontSize: 12, fontFamily: 'monospace',
                                                              color: a.por_cobrar > 0 ? '#b91c1c' : '#94a3b8',
                                                              display: { xs: 'none', md: 'table-cell' } }}>{fmt(a.por_cobrar)}</TableCell>
                              <TableCell align="center">
                                <Chip label={`${pct}%`} size="small"
                                  sx={{ height: 20, fontSize: 11, fontWeight: 700,
                                        bgcolor: pct >= 80 ? '#dcfce7' : pct >= 50 ? '#fef3c7' : '#fee2e2',
                                        color:   pct >= 80 ? '#15803d' : pct >= 50 ? '#a16207' : '#b91c1c' }} />
                              </TableCell>
                            </TableRow>
                            {/* Fila expandible: reservas de esta agencia con sus vouchers */}
                            <TableRow>
                              <TableCell colSpan={8} sx={{ p: 0, border: 0 }}>
                                <Collapse in={abierta} timeout="auto" unmountOnExit>
                                  <Box sx={{ p: 1.5, bgcolor: '#f0fdfa',
                                              borderBottom: '1px solid #99f6e4' }}>
                                    {misReservas.length === 0 ? (
                                      <Typography sx={{ fontSize: 11, color: '#64748b',
                                                          fontStyle: 'italic' }}>
                                        Sin reservas para mostrar.
                                      </Typography>
                                    ) : (
                                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                        {misReservas.map(rr => (
                                          <Box key={rr.id} sx={{
                                            display: 'flex', gap: 1, alignItems: 'center',
                                            p: 0.75, borderRadius: 1, bgcolor: '#fff',
                                            border: '1px solid #e2e8f0', flexWrap: 'wrap',
                                          }}>
                                            <Typography sx={{ fontSize: 11, fontFamily: 'monospace',
                                                                fontWeight: 700, color: '#0f172a' }}>
                                              {rr.codigo}
                                            </Typography>
                                            <Typography sx={{ fontSize: 11, color: '#334155', flex: 1 }}>
                                              {rr.cliente}
                                            </Typography>
                                            <Typography sx={{ fontSize: 11, fontFamily: 'monospace' }}>
                                              PVP {fmt(rr.pvp)}
                                            </Typography>
                                            <Typography sx={{ fontSize: 11, fontFamily: 'monospace',
                                                                color: '#0d9488' }}>
                                              Cobr. {fmt(rr.cobrado)}
                                            </Typography>
                                            <VouchersCell
                                              vouchers={rr.vouchers}
                                              onVer={setVoucherLightbox}
                                              compact
                                            />
                                          </Box>
                                        ))}
                                      </Box>
                                    )}
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          {/* Distribución por salida futura */}
          {salidasFuturas.length > 0 && (
            <Card sx={{ mb: 2, borderRadius: 2 }}>
              <CardContent sx={{ p: 2 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#0f172a', mb: 1 }}>
                  Distribución por día de salida (futuras)
                </Typography>
                <Typography sx={{ fontSize: 11, color: '#64748b', mb: 1.5 }}>
                  Qué días futuros están recibiendo estas ventas de hoy — útil para dimensionar embarcaciones.
                </Typography>
                <Box sx={{ display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,1fr)', md: 'repeat(3,1fr)', lg: 'repeat(4,1fr)' },
                            gap: 1 }}>
                  {salidasFuturas.map(s => (
                    <Box key={s.fecha_salida} sx={{
                      p: 1.25, bgcolor: '#eff6ff', border: '1px solid #bfdbfe',
                      borderRadius: 1.5,
                    }}>
                      <Typography sx={{ fontSize: 10, color: '#1e40af', fontWeight: 700, textTransform: 'uppercase' }}>
                        {s.fecha_salida}
                      </Typography>
                      <Typography sx={{ fontSize: 15, fontWeight: 800, color: '#0f172a', fontFamily: 'monospace' }}>
                        {fmt(s.pvp)}
                      </Typography>
                      <Typography sx={{ fontSize: 10, color: '#64748b' }}>
                        {s.num_reservas} {s.num_reservas === 1 ? 'reserva' : 'reservas'}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          )}

        </>
      ))}

      {/* ── Lente: FECHA DE SALIDA ───────────────────────────────────
          El libro hacia adelante. Sólo aparecen los días QUE TIENEN
          ventas: no tiene sentido pintar un calendario lleno de casillas
          vacías cuando la pregunta es exactamente la contraria. */}
      {lente === 'salida' && (cargandoSalida || !dataSalida ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}><CircularProgress /></Box>
      ) : (
        <>
          <Card sx={{ mb: 2, borderRadius: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center',
                          justifyContent: 'space-between', mb: 1.5, gap: 1, flexWrap: 'wrap' }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#0f172a' }}>
                    Días con ventas
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: '#64748b' }}>
                    Desde hoy en adelante · toca un día para ver sus reservas
                  </Typography>
                </Box>
                <IconButton size="small" onClick={() => refetchSalida()} disabled={refrescandoSalida}>
                  <RefreshIcon sx={{ fontSize: 18,
                    animation: refrescandoSalida ? 'spin 1s linear infinite' : 'none' }} />
                </IconButton>
              </Box>

              {/* Totales del rango — mismo patrón de tile que Venta diaria,
                  con el valor completo y no abreviado. */}
              <Box sx={{ display: 'grid', gap: 1, mb: 2, alignItems: 'stretch',
                          gridAutoRows: '1fr',
                          gridTemplateColumns: {
                            xs: 'repeat(2, minmax(0,1fr))',
                            sm: 'repeat(3, minmax(0,1fr))',
                            md: 'repeat(5, minmax(0,1fr))' } }}>
                {[
                  { t: 'Días con ventas', v: dataSalida.resumen.num_dias, money: false, c: '#0f172a', bg: '#f1f5f9' },
                  { t: 'Reservas',        v: dataSalida.resumen.num_reservas, money: false, c: '#0f172a', bg: '#f1f5f9' },
                  { t: 'Pasajeros',       v: dataSalida.resumen.pax, money: false, c: '#0369a1', bg: '#e0f2fe' },
                  { t: 'PVP vendido',     v: dataSalida.resumen.pvp, money: true,  c: '#15803d', bg: '#dcfce7' },
                  { t: 'Por cobrar',      v: dataSalida.resumen.por_cobrar, money: true, c: '#b45309', bg: '#fef3c7' },
                ].map(k => (
                  <Box key={k.t} sx={{ p: 1.25, borderRadius: 1.5, bgcolor: k.bg,
                                        display: 'flex', flexDirection: 'column',
                                        justifyContent: 'center', minWidth: 0 }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#64748b',
                                       textTransform: 'uppercase', letterSpacing: .4 }}>
                      {k.t}
                    </Typography>
                    <Typography sx={{ fontSize: { xs: 14, md: 16 }, fontWeight: 800, color: k.c,
                                       fontVariantNumeric: 'tabular-nums', wordBreak: 'break-word' }}>
                      {k.money ? fmt(k.v) : k.v}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {dataSalida.dias.length === 0 && (
                <Typography sx={{ fontSize: 13, color: '#64748b', textAlign: 'center', py: 4 }}>
                  No hay ventas registradas para ninguna fecha futura.
                </Typography>
              )}

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {dataSalida.dias.map(d => {
                  const abierto = diasAbiertos.has(d.fecha_salida);
                  const chip = _chipContexto(d.fecha_salida);
                  const pct = d.pvp > 0 ? Math.round(d.cobrado / d.pvp * 100) : 0;
                  return (
                    <Box key={d.fecha_salida} sx={{ border: '1px solid #e2e8f0',
                                                     borderRadius: 1.5, overflow: 'hidden' }}>
                      <Box onClick={() => toggleDia(d.fecha_salida)} role="button" tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleDia(d.fecha_salida); }}
                        sx={{ p: 1.25, cursor: 'pointer', bgcolor: abierto ? '#f8fafc' : '#fff',
                               display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
                               '&:hover': { bgcolor: '#f8fafc' } }}>
                        <ExpandMoreIcon sx={{ fontSize: 18, color: '#94a3b8', flexShrink: 0,
                          transform: abierto ? 'rotate(180deg)' : 'none', transition: 'transform .18s' }} />
                        <Box sx={{ flex: '1 1 190px', minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: .75, flexWrap: 'wrap' }}>
                            <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: '#0f172a' }}>
                              {_fechaHumana(d.fecha_salida)}
                            </Typography>
                            {chip && (
                              <Chip label={chip.label} size="small"
                                sx={{ height: 18, fontSize: 9.5, fontWeight: 800,
                                       bgcolor: chip.bg, color: chip.color }} />
                            )}
                          </Box>
                          <Typography sx={{ fontSize: 10.5, color: '#64748b',
                                             overflow: 'hidden', textOverflow: 'ellipsis',
                                             whiteSpace: 'nowrap' }}>
                            {d.paquetes.join(' · ') || '—'}
                          </Typography>
                        </Box>
                        {/* Cifras del día. En móvil bajan a su propia fila
                            completa: cortar el monto es justo perder el dato
                            por el que se abre esta pantalla. */}
                        <Box sx={{ display: 'flex', gap: { xs: 1.25, sm: 2 }, flexWrap: 'wrap',
                                    flex: { xs: '1 1 100%', sm: '0 0 auto' },
                                    justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
                          {[
                            { t: 'Reservas', v: String(d.num_reservas), c: '#0f172a' },
                            { t: 'Pax',      v: String(d.pax),          c: '#0369a1' },
                            { t: 'PVP',      v: fmt(d.pvp),             c: '#15803d' },
                            { t: 'Por cobrar', v: fmt(d.por_cobrar),
                              c: d.por_cobrar > 0 ? '#b45309' : '#94a3b8' },
                          ].map(x => (
                            <Box key={x.t} sx={{ minWidth: 0 }}>
                              <Typography sx={{ fontSize: 9, fontWeight: 700, color: '#94a3b8',
                                                 textTransform: 'uppercase', letterSpacing: .4 }}>
                                {x.t}
                              </Typography>
                              <Typography sx={{ fontSize: 13, fontWeight: 800, color: x.c,
                                                 fontVariantNumeric: 'tabular-nums' }}>
                                {x.v}
                              </Typography>
                            </Box>
                          ))}
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: 9, fontWeight: 700, color: '#94a3b8',
                                               textTransform: 'uppercase', letterSpacing: .4 }}>
                              Cobrado
                            </Typography>
                            <Typography sx={{ fontSize: 13, fontWeight: 800,
                                               color: pct >= 100 ? '#15803d' : '#64748b',
                                               fontVariantNumeric: 'tabular-nums' }}>
                              {pct}%
                            </Typography>
                          </Box>
                        </Box>
                      </Box>

                      <Collapse in={abierto} unmountOnExit>
                        <Box sx={{ borderTop: '1px solid #e2e8f0', bgcolor: '#fff' }}>
                          {(d.reservas || []).map(r => (
                            <Box key={r.id} sx={{ px: 1.5, py: 1, display: 'flex', gap: 1,
                                                   alignItems: 'center', flexWrap: 'wrap',
                                                   borderBottom: '1px solid #f1f5f9' }}>
                              <Box sx={{ flex: '1 1 220px', minWidth: 0 }}>
                                <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#0f172a',
                                                   fontFamily: 'monospace' }}>
                                  {r.codigo}
                                </Typography>
                                <Typography sx={{ fontSize: 11.5, color: '#475569' }}>
                                  {r.cliente}
                                  {r.agencia ? ` · ${r.agencia}` : ''}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', gap: 1.75, flexWrap: 'wrap',
                                          flex: { xs: '1 1 100%', sm: '0 0 auto' } }}>
                                <Typography sx={{ fontSize: 11.5, color: '#0369a1', fontWeight: 700 }}>
                                  {r.pax} pax
                                </Typography>
                                <Typography sx={{ fontSize: 11.5, color: '#15803d', fontWeight: 700,
                                                   fontVariantNumeric: 'tabular-nums' }}>
                                  {fmt(r.pvp)}
                                </Typography>
                                {r.por_cobrar > 0 && (
                                  <Typography sx={{ fontSize: 11.5, color: '#b45309', fontWeight: 700,
                                                     fontVariantNumeric: 'tabular-nums' }}>
                                    debe {fmt(r.por_cobrar)}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </Collapse>
                    </Box>
                  );
                })}
              </Box>
            </CardContent>
          </Card>
        </>
      ))}

      {/* Lightbox global de voucher — mismo patrón que en Cierre de Muelle. */}
      <VoucherLightbox voucher={voucherLightbox} onClose={() => setVoucherLightbox(null)} />

      <style>{`@keyframes spin { 100% { transform: rotate(360deg); } }`}</style>
    </Box>
  );
}

/** Tabla de reservas creadas hoy, parametrizada para pasadías o charter.
 *  - `esCharter` desactiva la columna Comercial y muestra "Charter directo"
 *    en Agencia (los charters son venta directa de Entretur, no llevan
 *    intermediario y por tanto no aplica comisión ni ranking).
 *  - Colores del header ligeramente distintos para diferenciar visualmente
 *    las dos tablas cuando ambas están presentes.
 *  - Responsivo:
 *      · xs / sm  → cards apiladas (imposible caber 9 col en móvil)
 *      · md       → tabla condensada (oculta "Antes" cuando no aporta y
 *                    muestra montos en formato compacto para ganar aire)
 *      · lg       → tabla completa con todas las columnas
 */
function ReservasTable({ titulo, reservas, colorHeader = '#0d9488', onVerVoucher, esCharter = false }) {
  // Si ninguna reserva tiene "cobrado antes" ocultamos la columna en md
  // (en ese breakpoint el ancho es crítico; en lg siempre se muestra).
  const hayAntes = reservas.some(r => (r.cobrado_antes || 0) > 0);

  return (
    <Card sx={{ mb: 2, borderRadius: 2 }}>
      <CardContent sx={{ p: { xs: 1.25, md: 2 } }}>
        <Typography sx={{ fontSize: 14, fontWeight: 800, color: colorHeader, mb: 1,
                          display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 4, height: 14, bgcolor: colorHeader, borderRadius: 0.5 }} />
          {titulo} ({reservas.length})
        </Typography>
        {reservas.length === 0 ? (
          <Typography sx={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }}>
            Sin {esCharter ? 'charters' : 'pasadías'} en la fecha seleccionada.
          </Typography>
        ) : (
          // Vista tipo hoja de cálculo: todas las columnas visibles siempre,
          // scroll horizontal cuando el ancho no alcanza. Sin ocultamientos
          // responsivos ni cifras compactas — priorizamos contexto sobre
          // adaptación al viewport.
          <TableContainer component={Paper}
            sx={{
              boxShadow: 'none', border: '1px solid #e2e8f0',
              overflowX: 'auto',
              maxHeight: 'calc(100vh - 320px)',   // habilita scroll vertical → sticky header funciona
              '& .MuiTableCell-root': { px: 1, whiteSpace: 'nowrap' },
              // --- STICKY HEADER (fila superior siempre visible al scrollear vertical) ---
              '& thead th': {
                position: 'sticky', top: 0, zIndex: 2,
                bgcolor: '#f8fafc',
              },
              // --- STICKY COLUMNAS 1-3 (Código, Cliente, Agencia siempre visibles al scrollear horizontal) ---
              '& tr > th:nth-of-type(1), & tr > td:nth-of-type(1)': {
                position: 'sticky', left: 0, zIndex: 3,
                bgcolor: '#fff',
                borderRight: '1px solid #e2e8f0',
                minWidth: 130, maxWidth: 160,
              },
              '& tr > th:nth-of-type(2), & tr > td:nth-of-type(2)': {
                position: 'sticky', left: 130, zIndex: 3,
                bgcolor: '#fff',
                borderRight: '1px solid #e2e8f0',
                minWidth: 180, maxWidth: 220,
              },
              '& tr > th:nth-of-type(3), & tr > td:nth-of-type(3)': {
                position: 'sticky', left: 310, zIndex: 3,     // 130 + 180 (offset acumulado)
                bgcolor: '#fff',
                borderRight: '2px solid #cbd5e1',              // borde más marcado en la última col ancla
                minWidth: 150, maxWidth: 180,
              },
              // Esquina superior izquierda: header + columnas sticky → z-index mayor
              '& thead tr > th:nth-of-type(1), & thead tr > th:nth-of-type(2), & thead tr > th:nth-of-type(3)': {
                zIndex: 4, bgcolor: '#f8fafc',
              },
              // Footer sticky abajo + esquinas sticky para las 3 columnas ancla
              '& tfoot td': {
                position: 'sticky', bottom: 0, zIndex: 2,
                bgcolor: '#f1f5f9',
              },
              '& tfoot tr > td:nth-of-type(1), & tfoot tr > td:nth-of-type(2), & tfoot tr > td:nth-of-type(3)': {
                zIndex: 4, bgcolor: '#f1f5f9',
              },
              // Hover row: pintamos también las celdas sticky para que sigan la fila
              '& tbody tr:hover > td:nth-of-type(1), & tbody tr:hover > td:nth-of-type(2), & tbody tr:hover > td:nth-of-type(3)': {
                bgcolor: '#f8fafc',
              },
            }}>
            <Table size="small" sx={{ tableLayout: 'auto' }}>
              <TableHead sx={{ bgcolor: '#f8fafc' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Código</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Cliente</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Agencia</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>
                    {esCharter ? 'Vendedor' : 'Comercial'}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Salida</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11 }}>PVP</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11, color: '#7c3aed' }}>
                    <Tooltip title="Comisión anticipada: la agencia ya la retuvo del pago del cliente, Entretur no la debe">
                      <span>Com. anticipada</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11, color: '#be185d' }}>
                    <Tooltip title="Comisión por pagar: Entretur aún debe liquidar este monto a la agencia">
                      <span>Com. por pagar</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11, color: '#0d9488' }}>
                    <Tooltip title="Neto para Entretur = PVP − comisión de la agencia (lo que efectivamente ingresa a la operación)">
                      <span>Ingreso neto</span>
                    </Tooltip>
                  </TableCell>
                  {/* Orden temporal izquierda→derecha: Antes → HOY → Después → Saldo.
                      Cada bloque tiene su color: azul (antes), verde (hoy),
                      ámbar (después). El saldo es el snapshot al día. */}
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11, color: '#1d4ed8',
                                                  bgcolor: '#dbeafe' }}>
                    <Tooltip title="Cobros registrados en días anteriores al día seleccionado"><span>Cobr. antes</span></Tooltip>
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, fontSize: 11, color: '#1d4ed8',
                                                   bgcolor: '#dbeafe' }}>
                    <Tooltip title="Vouchers con fecha del pago anterior al día seleccionado">
                      <span>Vouch. antes</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11, color: '#15803d',
                                                  bgcolor: '#f0fdf4' }}>
                    <Tooltip title="Ingresó a cuenta en la fecha seleccionada"><span>Cobr. HOY</span></Tooltip>
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, fontSize: 11, color: '#15803d',
                                                    bgcolor: '#f0fdf4' }}>
                    <Tooltip title="Vouchers con fecha del pago = fecha seleccionada">
                      <span>Vouch. HOY</span>
                    </Tooltip>
                  </TableCell>
                  {/* BLOQUE MUELLE (naranja tenue) — snapshot del pendiente + narración del pago posterior. */}
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11, color: '#a16207', bgcolor: '#fef3c7' }}>
                    <Tooltip title="Saldo que quedaba por cobrar en muelle al cierre del día seleccionado (snapshot histórico, no cambia si se paga después)">
                      <span>Por cobrar muelle</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11, color: '#a16207', bgcolor: '#fef3c7' }}>
                    <Tooltip title="Monto pagado en muelle después del día seleccionado (si aún era pendiente)">
                      <span>Pago muelle</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, fontSize: 11, color: '#a16207', bgcolor: '#fef3c7' }}>
                    <Tooltip title="Fecha en que se cobró el pendiente de muelle">
                      <span>Fecha muelle</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, fontSize: 11, color: '#a16207', bgcolor: '#fef3c7' }}>
                    <Tooltip title="Voucher o 'Efectivo' según el método usado">
                      <span>Evidencia muelle</span>
                    </Tooltip>
                  </TableCell>
                  {/* BLOQUE AGENCIA (rojo tenue) — snapshot del pendiente + narración del pago posterior. */}
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11, color: '#b91c1c', bgcolor: '#fee2e2' }}>
                    <Tooltip title="Saldo que la agencia debía cubrir al cierre del día seleccionado (snapshot histórico, no cambia si se paga después)">
                      <span>Por cobrar agencia</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11, color: '#b91c1c', bgcolor: '#fee2e2' }}>
                    <Tooltip title="Monto pagado por la agencia después del día seleccionado">
                      <span>Pago agencia</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, fontSize: 11, color: '#b91c1c', bgcolor: '#fee2e2' }}>
                    <Tooltip title="Fecha en que la agencia pagó su pendiente">
                      <span>Fecha agencia</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, fontSize: 11, color: '#b91c1c', bgcolor: '#fee2e2' }}>
                    <Tooltip title="Voucher que soporta la transferencia de la agencia">
                      <span>Evidencia agencia</span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reservas.map(r => (
                  <TableRow key={r.id} hover>
                    {/* Código en monospace + badge de estado si es problemático
                        (no_show, cancelada). Estados normales no muestran badge. */}
                    <TableCell sx={{ fontSize: 12, fontFamily: 'monospace' }}>
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                        <span>{r.codigo}</span>
                        <BadgeEstadoReserva estado={r.estado} />
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{r.cliente || '—'}</TableCell>
                    <TableCell sx={{ fontSize: 11,
                                      color: r.agencia_nombre ? '#0f172a' : '#94a3b8',
                                      fontStyle: r.agencia_nombre ? 'normal' : 'italic' }}>
                      {r.agencia_nombre || 'Venta directa'}
                    </TableCell>
                    <TableCell sx={{ fontSize: 11, color: '#64748b' }}>{r.comercial || '—'}</TableCell>
                    <TableCell sx={{ fontSize: 11 }}>
                      {r.salida_fecha || '—'}
                      {r.paquete && (
                        <Typography component="span" sx={{ fontSize: 10, color: '#94a3b8', ml: 0.5 }}>
                          · {r.paquete}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: 12, fontFamily: 'monospace' }}>{fmt(r.pvp)}</TableCell>
                    <TableCell align="right" sx={{ fontSize: 12, fontFamily: 'monospace',
                                                    color: (r.comision_anticipada || 0) > 0 ? '#7c3aed' : '#cbd5e1' }}>
                      {fmt(r.comision_anticipada || 0)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: 12, fontFamily: 'monospace',
                                                    color: (r.comision_por_pagar || 0) > 0 ? '#be185d' : '#cbd5e1' }}>
                      {fmt(r.comision_por_pagar || 0)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: 12, fontFamily: 'monospace', color: '#0d9488' }}>
                      {fmt(r.neto)}
                    </TableCell>
                    {/* Bloque ANTES (azul) — pagos anteriores al día.
                        Monto grande + chips de método debajo. */}
                    <TableCell align="right" sx={{ fontSize: 12, fontFamily: 'monospace',
                                                    color: r.cobrado_antes > 0 ? '#1d4ed8' : '#94a3b8',
                                                    bgcolor: '#dbeafe' }}>
                      <MontoConChipsMetodo monto={r.cobrado_antes} pagos={r.pagos_antes_detalle} />
                    </TableCell>
                    <TableCell align="center" sx={{ bgcolor: '#dbeafe' }}>
                      <VouchersCell vouchers={r.vouchers_antes || []} onVer={onVerVoucher} />
                    </TableCell>
                    {/* Bloque HOY (verde) — pagos del día. Monto + chips de método. */}
                    <TableCell align="right" sx={{ fontSize: 12, fontFamily: 'monospace',
                                                    color: r.cobrado_hoy > 0 ? '#15803d' : '#94a3b8',
                                                    fontWeight: r.cobrado_hoy > 0 ? 700 : 400,
                                                    bgcolor: '#f0fdf4' }}>
                      <MontoConChipsMetodo monto={r.cobrado_hoy} pagos={r.pagos_hoy_detalle} />
                    </TableCell>
                    <TableCell align="center" sx={{ bgcolor: '#f0fdf4' }}>
                      <VouchersCell vouchers={r.vouchers_hoy || []} onVer={onVerVoucher} />
                    </TableCell>
                    {/* Bloques MUELLE + AGENCIA — 4 celdas cada uno.
                        Los pagos posteriores se atribuyen según saldo_a_cargo_de
                        del día: si el pendiente era del muelle, van al bloque
                        muelle; si era de la agencia, al bloque agencia. Match
                        voucher-pago por monto exacto y misma fecha. */}
                    {(() => {
                      const saldo = r.saldo_al_dia || 0;
                      const esAgencia = r.saldo_a_cargo_de === 'agencia';
                      const porMuelle = saldo > 0 && !esAgencia ? saldo : 0;
                      const porAgencia = saldo > 0 && esAgencia ? saldo : 0;
                      const detallesPost = r.pagos_despues_detalle || [];
                      const vouchersPost = r.vouchers_despues || [];
                      const pagosMuelle = porMuelle > 0 ? detallesPost : [];
                      const pagosAgencia = porAgencia > 0 ? detallesPost : [];
                      return (
                        <>
                          {/* MUELLE — 4 celdas. Tooltip con desglose neto/custodia
                              solo aparece cuando hay comisión por pagar (parte
                              del saldo que Entretur retiene para la agencia). */}
                          <TableCell align="right" sx={{ fontSize: 12, fontFamily: 'monospace',
                                                          color: porMuelle > 0 ? '#a16207' : '#cbd5e1', bgcolor: '#fef3c7',
                                                          cursor: (r.saldo_al_dia_custodia || 0) > 0 ? 'help' : 'default' }}>
                            {(r.saldo_al_dia_custodia || 0) > 0 && porMuelle > 0 ? (
                              <Tooltip arrow title={<span style={{ whiteSpace: 'pre-line' }}>{
                                `Total a cobrar en muelle: ${fmt(porMuelle)}\n` +
                                `  · Neto Entretur: ${fmt(r.saldo_al_dia_neto || 0)}\n` +
                                `  · Custodia comisión agencia: ${fmt(r.saldo_al_dia_custodia)}`
                              }</span>}>
                                <span>{fmt(porMuelle)}</span>
                              </Tooltip>
                            ) : fmt(porMuelle)}
                          </TableCell>
                          <CeldaPagoMonto pagos={pagosMuelle} colorActivo="#a16207" bg="#fef3c7" />
                          <CeldaPagoFecha pagos={pagosMuelle} colorActivo="#a16207" bg="#fef3c7" />
                          <CeldaEvidencia pagos={pagosMuelle} vouchers={vouchersPost} bg="#fef3c7" onVerVoucher={onVerVoucher} />
                          {/* AGENCIA — 4 celdas */}
                          <TableCell align="right" sx={{ fontSize: 12, fontFamily: 'monospace',
                                                          color: porAgencia > 0 ? '#b91c1c' : '#cbd5e1', bgcolor: '#fee2e2' }}>
                            {fmt(porAgencia)}
                          </TableCell>
                          <CeldaPagoMonto pagos={pagosAgencia} colorActivo="#b91c1c" bg="#fee2e2" />
                          <CeldaPagoFecha pagos={pagosAgencia} colorActivo="#b91c1c" bg="#fee2e2" />
                          <CeldaEvidencia pagos={pagosAgencia} vouchers={vouchersPost} bg="#fee2e2" onVerVoucher={onVerVoucher} />
                        </>
                      );
                    })()}
                  </TableRow>
                ))}
              </TableBody>
              {/* FOOTER — sumatoria de todas las columnas de dinero.
                  Colores por bloque para mantener coherencia visual con
                  el header y las celdas del cuerpo. Border-top grueso para
                  separar visualmente del cuerpo. */}
              <TotalesReservas reservas={reservas} />
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
}

/** DEPRECATED — se prefirió la vista tabla (más contextual para ver
 *  ventas + vouchers juntos). Mantenido temporalmente por si volvemos
 *  a mobile-first en otra pantalla. */
// eslint-disable-next-line no-unused-vars
function ReservaCardMobile({ r, onVerVoucher }) {
  const cargo = r.saldo_a_cargo_de || 'muelle';
  const saldoColor = r.saldo_pendiente > 0
    ? (cargo === 'agencia' ? '#b91c1c' : '#a16207')
    : '#94a3b8';
  return (
    <Box sx={{
      p: 1.25, borderRadius: 1.5, border: '1px solid #e2e8f0',
      bgcolor: '#fff',
    }}>
      {/* Header: código + cliente */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
                  gap: 1, mb: 0.75, pb: 0.75, borderBottom: '1px dashed #e2e8f0' }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontSize: 11, fontFamily: 'monospace', fontWeight: 700,
                              color: '#0f172a', letterSpacing: 0.2 }}>
            {r.codigo}
          </Typography>
          <Typography sx={{ fontSize: 13, color: '#334155', fontWeight: 600,
                              overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {r.cliente || '—'}
          </Typography>
          {r.comercial && (
            <Typography sx={{ fontSize: 10, color: '#94a3b8' }}>
              por {r.comercial}
            </Typography>
          )}
        </Box>
        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
          <Typography sx={{ fontSize: 9, color: '#94a3b8', textTransform: 'uppercase',
                              letterSpacing: 0.4, fontWeight: 700 }}>
            Salida
          </Typography>
          <Typography sx={{ fontSize: 11, color: '#334155', fontWeight: 600 }}>
            {r.salida_fecha || '—'}
          </Typography>
          {r.paquete && (
            <Typography sx={{ fontSize: 9, color: '#94a3b8' }}>{r.paquete}</Typography>
          )}
        </Box>
      </Box>

      {/* Grid con métricas — 3 columnas para no romper la lectura */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.75, mb: 0.75 }}>
        <MetricMini label="PVP" value={r.pvp} color="#0f172a" />
        <MetricMini label="Cobrado HOY" value={r.cobrado_hoy}
                     color={r.cobrado_hoy > 0 ? '#15803d' : '#94a3b8'}
                     bold={r.cobrado_hoy > 0} />
        <MetricMini label={`Saldo${r.saldo_pendiente > 0 ? ` · ${cargo}` : ''}`}
                     value={r.saldo_pendiente} color={saldoColor} />
      </Box>

      {/* Cobrado antes (solo si aplica) + vouchers */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  gap: 1, flexWrap: 'wrap' }}>
        {(r.cobrado_antes || 0) > 0 ? (
          <Typography sx={{ fontSize: 10, color: '#0891b2', fontWeight: 600 }}>
            Cobrado antes: {fmt(r.cobrado_antes)}
          </Typography>
        ) : <span />}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography sx={{ fontSize: 9, color: '#94a3b8', textTransform: 'uppercase',
                              letterSpacing: 0.4, fontWeight: 700 }}>
            Vouchers
          </Typography>
          <VouchersCell vouchers={r.vouchers} onVer={onVerVoucher} compact />
        </Box>
      </Box>
    </Box>
  );
}

function MetricMini({ label, value, color, bold = false }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 9, color: '#94a3b8', textTransform: 'uppercase',
                          letterSpacing: 0.3, fontWeight: 700,
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 12, fontFamily: 'ui-monospace, monospace',
                          fontWeight: bold ? 800 : 600, color,
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                          fontVariantNumeric: 'tabular-nums' }}>
        {_fmtCompacto(value)}
      </Typography>
    </Box>
  );
}

/** Celda que muestra thumbnails de los vouchers de una reserva.
 *  Click en un thumbnail abre el lightbox. Si no hay vouchers → chip "—". */
function VouchersCell({ vouchers = [], onVer, compact = false }) {
  if (!vouchers || vouchers.length === 0) {
    return <Typography sx={{ fontSize: 10, color: '#cbd5e1' }}>—</Typography>;
  }
  const size = compact ? 26 : 32;
  const visibles = vouchers.slice(0, 3);
  const extra = vouchers.length - visibles.length;

  return (
    <Box sx={{ display: 'inline-flex', gap: 0.4, alignItems: 'center' }}>
      {visibles.map(v => (
        <Tooltip key={v.id} arrow title={
          `Voucher #${v.id} · ${v.tipo || 'sin tipo'}` +
          (v.monto ? ` · $${new Intl.NumberFormat('es-CO').format(v.monto)}` : '') +
          (v.referencia ? ` · ref ${v.referencia}` : '') +
          '\nClick para ampliar'
        }>
          <Box
            onClick={(e) => { e.stopPropagation(); onVer(v); }}
            sx={{
              width: size, height: size, borderRadius: 0.75,
              bgcolor: '#f1f5f9', border: '1px solid #cbd5e1',
              overflow: 'hidden', cursor: 'pointer', flexShrink: 0,
              transition: 'transform 0.15s',
              '&:hover': { transform: 'scale(1.1)', borderColor: '#0d9488' },
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {v.url ? (
              <img src={v.url} alt="voucher"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <AttachFileIcon sx={{ fontSize: size * 0.5, color: '#94a3b8' }} />
            )}
          </Box>
        </Tooltip>
      ))}
      {extra > 0 && (
        <Tooltip title={`${extra} voucher(s) más`}>
          <Chip label={`+${extra}`} size="small"
            onClick={(e) => { e.stopPropagation(); onVer(vouchers[3]); }}
            sx={{ height: size - 8, fontSize: 10, fontWeight: 700,
                  bgcolor: '#e0e7ff', color: '#3730a3', cursor: 'pointer' }} />
        </Tooltip>
      )}
    </Box>
  );
}

/** Badge visual del estado de la reserva. Solo muestra chip para estados
 *  problemáticos (no_show, cancelada) que requieren atención — los estados
 *  normales (confirmada, completada, hold) no distraen la vista. */
function BadgeEstadoReserva({ estado }) {
  const e = (estado || '').toLowerCase();
  const config = {
    no_show:     { label: '⚠️ NO SHOW',   bg: '#fee2e2', fg: '#991b1b', tip: 'El cliente no se presentó al embarque' },
    cancelada:   { label: '✕ CANCELADA', bg: '#f1f5f9', fg: '#475569', tip: 'Reserva cancelada' },
    suspendida:  { label: '⏸ SUSPENDIDA', bg: '#fef3c7', fg: '#a16207', tip: 'Reserva suspendida (clima, cupo, etc.)' },
    reprogramada:{ label: '↻ REPROG.',    bg: '#e0e7ff', fg: '#3730a3', tip: 'Reserva reprogramada' },
  };
  const c = config[e];
  if (!c) return null;   // confirmada, completada, hold, pendiente → sin badge
  return (
    <Tooltip arrow title={c.tip}>
      <Chip
        size="small"
        label={c.label}
        sx={{ height: 16, fontSize: 9, fontWeight: 800,
              bgcolor: c.bg, color: c.fg,
              '& .MuiChip-label': { px: 0.6 } }}
      />
    </Tooltip>
  );
}

/** Muestra el monto total del bucket + chips compactos de cada método real
 *  usado en ese bucket (efectivo, transferencia, tarjeta, bold, etc.).
 *  Los chips exponen los pagos que no siempre tienen voucher (como efectivo
 *  en muelle), garantizando que ningún pago quede "invisible" en la vista. */
function MontoConChipsMetodo({ monto, pagos = [] }) {
  const total = Number(monto || 0);
  // Agrupamos pagos por método y sumamos montos → un chip por método.
  const porMetodo = {};
  for (const p of pagos) {
    const m = (p.metodo || 'otro');
    porMetodo[m] = (porMetodo[m] || 0) + Number(p.monto || 0);
  }
  const chips = Object.entries(porMetodo).sort((a, b) => b[1] - a[1]);

  // Formato compacto para chip: $600.000 → $600k, $1.440.000 → $1.44M
  const cifraCompacta = (v) => {
    if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(v >= 10_000_000 ? 1 : 2).replace(/\.?0+$/, '')}M`;
    if (v >= 1_000) return `$${Math.round(v / 1_000)}k`;
    return `$${v}`;
  };
  const iconoMetodo = (m) => {
    switch (m) {
      case 'efectivo': return '💵';
      case 'tarjeta_credito':
      case 'tarjeta_debito': return '💳';
      case 'bold_link': return '🔗';
      case 'transferencia':
      case 'pago_directo_cliente':
      case 'pago_agencia': return '🏦';
      case 'muelle': return '⚓';
      case 'cruce':
      case 'compensacion': return '🔄';
      default: return '•';
    }
  };
  const colorMetodo = (m) => {
    switch (m) {
      case 'efectivo': return { bg: '#dcfce7', fg: '#15803d' };
      case 'transferencia':
      case 'pago_directo_cliente':
      case 'pago_agencia': return { bg: '#e0e7ff', fg: '#3730a3' };
      case 'tarjeta_credito':
      case 'tarjeta_debito': return { bg: '#fce7f3', fg: '#9d174d' };
      case 'bold_link': return { bg: '#fef3c7', fg: '#a16207' };
      case 'muelle': return { bg: '#fef3c7', fg: '#a16207' };
      case 'cruce':
      case 'compensacion': return { bg: '#ede9fe', fg: '#5b21b6' };  // morado tenue — cruce contable, sin cash
      default: return { bg: '#f1f5f9', fg: '#475569' };
    }
  };

  return (
    <Box>
      <Box sx={{ fontFamily: 'monospace' }}>{fmt(total)}</Box>
      {chips.length > 0 && (
        <Box sx={{ display: 'flex', gap: 0.3, mt: 0.35, flexWrap: 'wrap',
                    justifyContent: 'flex-end' }}>
          {chips.map(([m, v]) => {
            const c = colorMetodo(m);
            return (
              <Tooltip key={m} arrow title={`${_labelMetodo(m)} · ${fmt(v)}`}>
                <Chip
                  size="small"
                  label={`${iconoMetodo(m)} ${cifraCompacta(v)}`}
                  sx={{ height: 16, fontSize: 9, fontWeight: 700,
                        bgcolor: c.bg, color: c.fg,
                        '& .MuiChip-label': { px: 0.6 } }}
                />
              </Tooltip>
            );
          })}
        </Box>
      )}
    </Box>
  );
}

/** Fila de totales al pie de la tabla — sumatoria de todas las columnas
 *  numéricas. Los pagos posteriores se atribuyen a muelle/agencia según
 *  el saldo_a_cargo_de de cada reserva (misma regla que usa el cuerpo). */
function TotalesReservas({ reservas = [] }) {
  const t = reservas.reduce((acc, r) => {
    const saldo = r.saldo_al_dia || 0;
    const esAgencia = r.saldo_a_cargo_de === 'agencia';
    const porMuelle = saldo > 0 && !esAgencia ? saldo : 0;
    const porAgencia = saldo > 0 && esAgencia ? saldo : 0;
    // Suma de los pagos posteriores atribuidos a cada bloque
    const pagosPost = (r.pagos_despues_detalle || []).reduce((s, p) => s + Number(p.monto || 0), 0);
    const pagoMuelle = porMuelle > 0 ? pagosPost : 0;
    const pagoAgencia = porAgencia > 0 ? pagosPost : 0;
    return {
      pvp: acc.pvp + Number(r.pvp || 0),
      com_ant: acc.com_ant + Number(r.comision_anticipada || 0),
      com_pagar: acc.com_pagar + Number(r.comision_por_pagar || 0),
      neto: acc.neto + Number(r.neto || 0),
      cobrado_antes: acc.cobrado_antes + Number(r.cobrado_antes || 0),
      cobrado_hoy: acc.cobrado_hoy + Number(r.cobrado_hoy || 0),
      por_cobrar_muelle: acc.por_cobrar_muelle + porMuelle,
      pago_muelle: acc.pago_muelle + pagoMuelle,
      por_cobrar_agencia: acc.por_cobrar_agencia + porAgencia,
      pago_agencia: acc.pago_agencia + pagoAgencia,
    };
  }, {
    pvp: 0, com_ant: 0, com_pagar: 0, neto: 0,
    cobrado_antes: 0, cobrado_hoy: 0,
    por_cobrar_muelle: 0, pago_muelle: 0,
    por_cobrar_agencia: 0, pago_agencia: 0,
  });

  // Estilo base para todas las celdas del footer: bold, ligeramente más grande,
  // borde superior grueso. `bgcolor` se pasa por celda para respetar los bloques.
  const base = {
    fontSize: 12.5,
    fontWeight: 800,
    fontFamily: 'monospace',
    borderTop: '2px solid #94a3b8',
    borderBottom: 'none',
    py: 1,
  };
  const baseCentro = { ...base, fontFamily: 'inherit', fontWeight: 700 };

  return (
    <TableFooter sx={{ '& .MuiTableCell-root': { color: '#0f172a' } }}>
      <TableRow sx={{ bgcolor: '#f1f5f9' }}>
        {/* Código: vacío */}
        <TableCell sx={base} />
        {/* Cliente: label del total con conteo */}
        <TableCell sx={{ ...baseCentro, color: '#334155', fontSize: 12 }}>
          TOTAL · {reservas.length} {reservas.length === 1 ? 'reserva' : 'reservas'}
        </TableCell>
        {/* Agencia, Comercial, Salida: vacío */}
        <TableCell sx={base} />
        <TableCell sx={base} />
        <TableCell sx={base} />
        {/* PVP */}
        <TableCell align="right" sx={base}>{fmt(t.pvp)}</TableCell>
        {/* Com. anticipada · Com. por pagar */}
        <TableCell align="right" sx={{ ...base, color: t.com_ant > 0 ? '#7c3aed' : '#94a3b8' }}>
          {fmt(t.com_ant)}
        </TableCell>
        <TableCell align="right" sx={{ ...base, color: t.com_pagar > 0 ? '#be185d' : '#94a3b8' }}>
          {fmt(t.com_pagar)}
        </TableCell>
        {/* Ingreso neto */}
        <TableCell align="right" sx={{ ...base, color: '#0d9488' }}>{fmt(t.neto)}</TableCell>
        {/* Bloque ANTES (azul) */}
        <TableCell align="right" sx={{ ...base, bgcolor: '#bfdbfe', color: '#1d4ed8' }}>
          {fmt(t.cobrado_antes)}
        </TableCell>
        <TableCell align="center" sx={{ ...baseCentro, bgcolor: '#bfdbfe', color: '#94a3b8' }}>—</TableCell>
        {/* Bloque HOY (verde) — la estrella de este footer */}
        <TableCell align="right" sx={{ ...base, bgcolor: '#bbf7d0', color: '#15803d', fontSize: 13 }}>
          {fmt(t.cobrado_hoy)}
        </TableCell>
        <TableCell align="center" sx={{ ...baseCentro, bgcolor: '#bbf7d0', color: '#94a3b8' }}>—</TableCell>
        {/* Bloque MUELLE (naranja) */}
        <TableCell align="right" sx={{ ...base, bgcolor: '#fde68a', color: '#a16207' }}>
          {fmt(t.por_cobrar_muelle)}
        </TableCell>
        <TableCell align="right" sx={{ ...base, bgcolor: '#fde68a', color: t.pago_muelle > 0 ? '#a16207' : '#94a3b8' }}>
          {fmt(t.pago_muelle)}
        </TableCell>
        <TableCell align="center" sx={{ ...baseCentro, bgcolor: '#fde68a', color: '#94a3b8' }}>—</TableCell>
        <TableCell align="center" sx={{ ...baseCentro, bgcolor: '#fde68a', color: '#94a3b8' }}>—</TableCell>
        {/* Bloque AGENCIA (rojo) */}
        <TableCell align="right" sx={{ ...base, bgcolor: '#fecaca', color: '#b91c1c' }}>
          {fmt(t.por_cobrar_agencia)}
        </TableCell>
        <TableCell align="right" sx={{ ...base, bgcolor: '#fecaca', color: t.pago_agencia > 0 ? '#b91c1c' : '#94a3b8' }}>
          {fmt(t.pago_agencia)}
        </TableCell>
        <TableCell align="center" sx={{ ...baseCentro, bgcolor: '#fecaca', color: '#94a3b8' }}>—</TableCell>
        <TableCell align="center" sx={{ ...baseCentro, bgcolor: '#fecaca', color: '#94a3b8' }}>—</TableCell>
      </TableRow>
    </TableFooter>
  );
}

/** Celda "Pago (muelle|agencia)" — suma de todos los pagos posteriores del bloque.
 *  Si hay >1 pago, tooltip con detalle por pago. */
function CeldaPagoMonto({ pagos = [], colorActivo, bg }) {
  const total = pagos.reduce((s, p) => s + Number(p.monto || 0), 0);
  const detalle = pagos.length > 1
    ? pagos.map(p => `${_fechaCorta(p.fecha)} · ${fmt(p.monto)}`).join('\n')
    : '';
  const contenido = (
    <TableCell align="right" sx={{ fontSize: 12, fontFamily: 'monospace',
                                    color: total > 0 ? colorActivo : '#cbd5e1', bgcolor: bg,
                                    cursor: detalle ? 'help' : 'default' }}>
      {fmt(total)}
    </TableCell>
  );
  if (!detalle) return contenido;
  return (
    <Tooltip arrow title={<span style={{ whiteSpace: 'pre-line' }}>{detalle}</span>}>
      {contenido}
    </Tooltip>
  );
}

/** Celda "Fecha (muelle|agencia)" — última fecha si hay >1 pago. */
function CeldaPagoFecha({ pagos = [], colorActivo, bg }) {
  if (pagos.length === 0) {
    return <TableCell align="center" sx={{ fontSize: 11, color: '#cbd5e1', bgcolor: bg }}>—</TableCell>;
  }
  // Ordenamos por fecha desc y tomamos la más reciente. Si hay varias en
  // días distintos, el tooltip lista todas.
  const ordenados = [...pagos].sort((a, b) => (b.fecha || '').localeCompare(a.fecha || ''));
  const ultima = ordenados[0];
  const hayVarias = new Set(pagos.map(p => p.fecha)).size > 1;
  const detalle = hayVarias
    ? pagos.map(p => `${_fechaCorta(p.fecha)} · ${fmt(p.monto)}`).join('\n')
    : '';
  const contenido = (
    <TableCell align="center" sx={{ fontSize: 11, color: colorActivo, bgcolor: bg,
                                     cursor: detalle ? 'help' : 'default', whiteSpace: 'nowrap' }}>
      {_fechaCorta(ultima.fecha)}{hayVarias && ` +${pagos.length - 1}`}
    </TableCell>
  );
  if (!detalle) return contenido;
  return (
    <Tooltip arrow title={<span style={{ whiteSpace: 'pre-line' }}>{detalle}</span>}>
      {contenido}
    </Tooltip>
  );
}

/** Celda "Evidencia (muelle|agencia)" —
 *  - Si algún pago es efectivo → chip "💵 Efectivo"
 *  - Si hay voucher con match de monto y fecha → thumbnail clickeable
 *  - Si no hay pagos → "—"
 *  - Si método no reconocido y sin voucher → texto del método */
function CeldaEvidencia({ pagos = [], vouchers = [], bg, onVerVoucher }) {
  if (pagos.length === 0) {
    return <TableCell align="center" sx={{ fontSize: 11, color: '#cbd5e1', bgcolor: bg }}>—</TableCell>;
  }
  // Match voucher-pago: mismo monto y misma fecha (fecha_pago_vision o created_at).
  const vouchersMatched = pagos.map(p => {
    const v = vouchers.find(vc => {
      const montoOK = Math.abs(Number(vc.monto || 0) - Number(p.monto || 0)) < 1;
      const fVoucher = vc.fecha_pago_vision || (vc.created_at ? vc.created_at.slice(0, 10) : null);
      const fechaOK = !p.fecha || !fVoucher || fVoucher === p.fecha;
      return montoOK && fechaOK;
    });
    return { pago: p, voucher: v };
  });

  return (
    <TableCell align="center" sx={{ bgcolor: bg }}>
      <Box sx={{ display: 'inline-flex', gap: 0.35, alignItems: 'center', flexWrap: 'wrap',
                  justifyContent: 'center' }}>
        {vouchersMatched.map(({ pago, voucher }, idx) => {
          const esEfectivo = (pago.metodo || '').toLowerCase() === 'efectivo';
          if (voucher) {
            return (
              <Tooltip key={idx} arrow title={
                `${_labelMetodo(pago.metodo)} · ${fmt(pago.monto)}` +
                (voucher.referencia ? ` · ref ${voucher.referencia}` : '') +
                '\nClick para ampliar'
              }>
                <Box onClick={(e) => { e.stopPropagation(); onVerVoucher(voucher); }}
                  sx={{ width: 26, height: 26, borderRadius: 0.75, bgcolor: '#f1f5f9',
                        border: '1px solid #cbd5e1', overflow: 'hidden', cursor: 'pointer',
                        flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        '&:hover': { transform: 'scale(1.1)', borderColor: '#0d9488' } }}>
                  {voucher.url
                    ? <img src={voucher.url} alt="voucher" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <AttachFileIcon sx={{ fontSize: 14, color: '#94a3b8' }} />}
                </Box>
              </Tooltip>
            );
          }
          // Sin voucher físico: efectivo, cruce, compensación… → chip con
          // icono/color específico del método. Cruce contable en morado
          // porque no representa un ingreso de cash (compensación de deuda).
          const m = (pago.metodo || '').toLowerCase();
          const iconoChip = m === 'efectivo' ? '💵'
                          : (m === 'cruce' || m === 'compensacion') ? '🔄'
                          : '•';
          const colorChip = m === 'efectivo' ? { bg: '#dcfce7', fg: '#15803d' }
                          : (m === 'cruce' || m === 'compensacion') ? { bg: '#ede9fe', fg: '#5b21b6' }
                          : { bg: '#f1f5f9', fg: '#475569' };
          const labelChip = m === 'efectivo' ? `${iconoChip} Efectivo`
                          : (m === 'cruce' || m === 'compensacion') ? `${iconoChip} Cruce ${fmt(pago.monto)}`
                          : _labelMetodo(pago.metodo);
          return (
            <Tooltip key={idx} arrow title={`${_labelMetodo(pago.metodo)} · ${fmt(pago.monto)}` +
                                             (pago.notas ? `\n${pago.notas}` : '')}>
              <Chip label={labelChip} size="small"
                sx={{ height: 20, fontSize: 10, fontWeight: 600,
                      bgcolor: colorChip.bg, color: colorChip.fg,
                      whiteSpace: 'pre-line',
                      '& .MuiChip-label': { whiteSpace: 'nowrap' } }} />
            </Tooltip>
          );
        })}
      </Box>
    </TableCell>
  );
}

/** Lightbox para ver el voucher completo. */
function VoucherLightbox({ voucher, onClose }) {
  if (!voucher) return null;
  const numFmt = new Intl.NumberFormat('es-CO');
  return (
    <Dialog open={!!voucher} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontSize: 14, fontWeight: 700, py: 1.5 }}>
        Voucher #{voucher.id} · {voucher.tipo || 'sin tipo'}
        {voucher.monto && ` · $${numFmt.format(voucher.monto)}`}
        {voucher.referencia && (
          <Box component="span" sx={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>
            {' '}· ref {voucher.referencia}
          </Box>
        )}
        {voucher.fecha_pago_vision && (
          <Box component="span" sx={{ fontSize: 11, color: '#0891b2', fontWeight: 500, ml: 1 }}>
            · fecha OCR: {voucher.fecha_pago_vision}
          </Box>
        )}
      </DialogTitle>
      <DialogContent sx={{ p: 0, bgcolor: '#0f172a', textAlign: 'center' }}>
        {voucher.url ? (
          <img src={voucher.url} alt="voucher"
            style={{ maxWidth: '100%', maxHeight: '75vh', display: 'block', margin: '0 auto' }} />
        ) : (
          <Typography sx={{ color: '#94a3b8', p: 4 }}>
            Este voucher no tiene imagen adjunta.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        {voucher.url && (
          <Button onClick={() => window.open(voucher.url, '_blank')} sx={{ textTransform: 'none' }}>
            Abrir en nueva pestaña
          </Button>
        )}
        <Button onClick={onClose} variant="contained" sx={{ textTransform: 'none' }}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Tile con desglose 2 sub-líneas (Pasadías / Charter). Mismo look que Tile
 *  regular pero con 2 líneas menores debajo del monto principal. */
function TileConBreakdown({ label, total, subA, subB, color, bg }) {
  const numFmt = new Intl.NumberFormat('es-CO', { maximumFractionDigits: 0 });
  const tooltipTxt =
    `${label} total: $${numFmt.format(Number(total || 0))}\n` +
    `${subA.label}: $${numFmt.format(Number(subA.v || 0))} (${subA.n} ${subA.n === 1 ? 'reserva' : 'reservas'})\n` +
    `${subB.label}: $${numFmt.format(Number(subB.v || 0))} (${subB.n} ${subB.n === 1 ? 'reserva' : 'reservas'})`;

  return (
    <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{tooltipTxt}</span>} arrow>
      <Box sx={{
        p: { xs: 1, md: 1.25 }, bgcolor: bg, borderRadius: 1.5,
        cursor: 'help',
        height: '100%',
        display: 'flex', flexDirection: 'column',
        justifyContent: 'space-between',
        minWidth: 0, overflow: 'hidden',
        gap: 0.25,
      }}>
        <Typography sx={{
          fontSize: { xs: 9, sm: 9, lg: 8.5 },
          color, fontWeight: 700, textTransform: 'uppercase',
          letterSpacing: 0.3, lineHeight: 1.2,
          alignSelf: 'flex-start',
          display: '-webkit-box', WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical', overflow: 'hidden',
          wordBreak: 'break-word',
        }}>
          {label}
        </Typography>
        <Box>
          {/* Valor total (grande) */}
          <Typography sx={{
            fontSize: { xs: 15, sm: 15, md: 14, lg: 13, xl: 15 },
            fontWeight: 800, color,
            fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
            lineHeight: 1.15,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            fontVariantNumeric: 'tabular-nums',
          }}>
            {_fmtCompacto(total)}
          </Typography>
          {/* 2 sub-líneas: Pasadías / Charter */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.15, mt: 0.25 }}>
            <SubLinea label={subA.label} v={subA.v} n={subA.n} color={color} />
            <SubLinea label={subB.label} v={subB.v} n={subB.n} color={color} />
          </Box>
        </Box>
      </Box>
    </Tooltip>
  );
}

function SubLinea({ label, v, n, color }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                gap: 0.5, minWidth: 0, opacity: n > 0 ? 1 : 0.4 }}>
      <Typography sx={{ fontSize: 8.5, color, fontWeight: 600,
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 9.5, fontWeight: 700, color,
                          fontFamily: 'ui-monospace, monospace',
                          whiteSpace: 'nowrap', textAlign: 'right' }}>
        {_fmtCompacto(v)}
      </Typography>
    </Box>
  );
}

// Formato compacto para tiles pequeños: $10,48M · $486k · $0
function _fmtCompacto(n) {
  const v = Number(n || 0);
  if (v === 0) return '$0';
  const abs = Math.abs(v);
  const sign = v < 0 ? '-' : '';
  if (abs >= 1_000_000) {
    const m = (abs / 1_000_000);
    const s = m >= 100 ? m.toFixed(0) : m.toFixed(2).replace(/\.?0+$/, '');
    return `${sign}$${s.replace('.', ',')}M`;
  }
  if (abs >= 1_000) {
    return `${sign}$${Math.round(abs / 1_000)}k`;
  }
  return `${sign}$${new Intl.NumberFormat('es-CO').format(abs)}`;
}

function Tile({ label, v, color, bg, hint }) {
  const valor = Number(v || 0);
  const textoExacto = `$${new Intl.NumberFormat('es-CO', { maximumFractionDigits: 0 }).format(valor)}`;
  const textoCompacto = _fmtCompacto(valor);

  // Tooltip muestra el valor exacto siempre que se compacte o cuando haya hint.
  const tooltipTxt = hint
    ? `${hint}${valor !== 0 ? ` · ${textoExacto}` : ''}`
    : (valor >= 1_000 ? textoExacto : '');

  const contenido = (
    <Box sx={{
      p: { xs: 1, md: 1.25 }, bgcolor: bg, borderRadius: 1.5,
      cursor: (hint || tooltipTxt) ? 'help' : 'default',
      height: '100%',
      display: 'flex', flexDirection: 'column',
      justifyContent: 'space-between',
      minWidth: 0, overflow: 'hidden',
      gap: 0.5,
    }}>
      <Typography sx={{
        // Label responsive — pequeño en tiles apretados
        fontSize: { xs: 9, sm: 9, lg: 8.5 },
        color, fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: 0.3, lineHeight: 1.2,
        borderBottom: hint ? '1px dotted currentColor' : 'none',
        alignSelf: 'flex-start',
        display: '-webkit-box', WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical', overflow: 'hidden',
        wordBreak: 'break-word',
      }}>
        {label}
      </Typography>
      <Typography sx={{
        // Valor: siempre compacto ($10,48M / $486k) para caber en 1 línea.
        // Fontsize responsive según ancho disponible en cada breakpoint.
        fontSize: { xs: 15, sm: 15, md: 14, lg: 13, xl: 15 },
        fontWeight: 800, color,
        fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
        lineHeight: 1.2,
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        fontVariantNumeric: 'tabular-nums',
      }}>
        {textoCompacto}
      </Typography>
    </Box>
  );

  return tooltipTxt
    ? <Tooltip title={tooltipTxt} arrow>{contenido}</Tooltip>
    : contenido;
}

// ============================================================================
// DateCalendarPopover — calendario visual mensual en un popover
// ----------------------------------------------------------------------------
// El input[type=date] nativo del navegador es inconsistente entre browsers y
// además el trick de "escondido con opacity:0 sobre una etiqueta" falla en
// algunos (Safari en particular no dispara el picker con click programático
// desde overlays). Este componente es 100% controlado con MUI: grid 7×N de
// días, navegación ‹ mes › y selectores de mes/año para saltos grandes.
//
// Uso:
//   <DateCalendarPopover
//     valor="2026-08-17"
//     onChange={iso => setFecha(iso)}
//     ancla={<Button>17 Ago 2026</Button>} />
// ============================================================================
function DateCalendarPopover({ valor, onChange, children }) {
  const [anchor, setAnchor] = React.useState(null);
  // Mes que se está viendo en el calendario — arranca en el mes del valor
  // seleccionado. Se resetea cada vez que se abre el popover para que
  // siempre veas el contexto del valor actual.
  const [vistaMes, setVistaMes] = React.useState(() => {
    if (!valor) return { y: new Date().getFullYear(), m: new Date().getMonth() + 1 };
    const [y, m] = valor.split('-').map(Number);
    return { y, m };
  });

  const abrir = (e) => {
    if (valor) {
      const [y, m] = valor.split('-').map(Number);
      setVistaMes({ y, m });
    }
    setAnchor(e.currentTarget);
  };
  const cerrar = () => setAnchor(null);

  const seleccionar = (dia) => {
    const iso = `${vistaMes.y}-${String(vistaMes.m).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
    onChange(iso);
    cerrar();
  };

  // Rejilla del mes: array de 42 celdas (6 semanas × 7 días) con nulls para
  // los espacios antes del día 1 y después del último día del mes.
  const celdas = React.useMemo(() => {
    const primerDia = new Date(vistaMes.y, vistaMes.m - 1, 1);
    const ultimoDia = new Date(vistaMes.y, vistaMes.m, 0).getDate();
    const offset = primerDia.getDay(); // 0=Dom, 1=Lun, ..., 6=Sab
    const out = [];
    for (let i = 0; i < offset; i++) out.push(null);
    for (let d = 1; d <= ultimoDia; d++) out.push(d);
    while (out.length % 7 !== 0) out.push(null);
    return out;
  }, [vistaMes]);

  const shiftMes = (delta) => {
    const total = vistaMes.y * 12 + (vistaMes.m - 1) + delta;
    setVistaMes({ y: Math.floor(total / 12), m: (total % 12) + 1 });
  };

  const meses = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
  const diasSemana = ['Do','Lu','Ma','Mi','Ju','Vi','Sa'];

  // Año: rango centrado en el año actual (± 5 años) para saltar rápido.
  const anioActual = new Date().getFullYear();
  const anios = Array.from({ length: 11 }, (_, i) => anioActual - 5 + i);

  // Día seleccionado y hoy — para pintar el estado en la rejilla.
  const [valY, valM, valD] = valor ? valor.split('-').map(Number) : [null, null, null];
  const hoy = new Date();
  const hoyMismoMes = hoy.getFullYear() === vistaMes.y && (hoy.getMonth() + 1) === vistaMes.m;
  const hoyDia = hoy.getDate();

  return (
    <>
      {/* El elemento que abre el popover — recibe el handler onClick */}
      {React.cloneElement(children, { onClick: abrir })}

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={cerrar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { p: 1.5, borderRadius: 2, minWidth: 280 } }}
      >
        {/* Header: navegación mes + selectores rápidos de mes/año */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
          <IconButton size="small" onClick={() => shiftMes(-1)} sx={{ color: '#0d9488' }}>
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <Select value={vistaMes.m}
            onChange={(e) => setVistaMes({ ...vistaMes, m: Number(e.target.value) })}
            size="small" variant="standard" disableUnderline
            sx={{ fontSize: 13, fontWeight: 700, color: '#0f172a',
                  '& .MuiSelect-select': { pr: '20px !important' } }}>
            {meses.map((n, i) => (
              <MenuItem key={i} value={i + 1} sx={{ fontSize: 13 }}>{n}</MenuItem>
            ))}
          </Select>
          <Select value={vistaMes.y}
            onChange={(e) => setVistaMes({ ...vistaMes, y: Number(e.target.value) })}
            size="small" variant="standard" disableUnderline
            sx={{ fontSize: 13, fontWeight: 700, color: '#0f172a',
                  '& .MuiSelect-select': { pr: '20px !important' } }}>
            {anios.map(y => (
              <MenuItem key={y} value={y} sx={{ fontSize: 13 }}>{y}</MenuItem>
            ))}
          </Select>
          <Box sx={{ flex: 1 }} />
          <IconButton size="small" onClick={() => shiftMes(+1)} sx={{ color: '#0d9488' }}>
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Header días de la semana */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.25, mb: 0.5 }}>
          {diasSemana.map(d => (
            <Typography key={d} sx={{ fontSize: 10, fontWeight: 700, color: '#94a3b8',
                                      textAlign: 'center', py: 0.5 }}>
              {d}
            </Typography>
          ))}
        </Box>

        {/* Grid de días */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.25 }}>
          {celdas.map((dia, i) => {
            if (dia === null) {
              return <Box key={i} sx={{ height: 32 }} />;
            }
            const esSeleccionado = valY === vistaMes.y && valM === vistaMes.m && valD === dia;
            const esHoy = hoyMismoMes && dia === hoyDia;
            return (
              <Box key={i}
                onClick={() => seleccionar(dia)}
                sx={{
                  height: 32,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', borderRadius: 1,
                  fontSize: 12, fontWeight: esSeleccionado || esHoy ? 700 : 500,
                  bgcolor: esSeleccionado ? '#0d9488'
                          : esHoy ? '#ccfbf1' : 'transparent',
                  color: esSeleccionado ? '#fff'
                         : esHoy ? '#0f766e' : '#0f172a',
                  border: esHoy && !esSeleccionado ? '1px solid #0d9488' : '1px solid transparent',
                  '&:hover': {
                    bgcolor: esSeleccionado ? '#0f766e' : '#f0fdfa',
                    borderColor: '#0d9488',
                  },
                  transition: 'all 0.1s',
                }}
              >
                {dia}
              </Box>
            );
          })}
        </Box>

        {/* Footer: acciones rápidas */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, pt: 1,
                    borderTop: '1px solid #f1f5f9' }}>
          <Button size="small" onClick={() => {
              const d = new Date();
              onChange(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
              cerrar();
            }}
            sx={{ textTransform: 'none', fontSize: 11, color: '#0d9488' }}>
            Hoy
          </Button>
          <Button size="small" onClick={cerrar}
            sx={{ textTransform: 'none', fontSize: 11, color: '#64748b' }}>
            Cerrar
          </Button>
        </Box>
      </Popover>
    </>
  );
}
